import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../firebase';
import { optimizeImage } from './imageOptimize';
import store from '../redux/store';
import { getSpecialtyStorageFolder } from './specialtyCollections';

/**
 * Upload an image to Firebase Storage and return the download URL
 * @param {File} file - The image file to upload
 * @param {string} folder - The folder path in Firebase Storage (default: 'question-bank-images')
 * @returns {Promise<string>} - The download URL of the uploaded image
 */
export const uploadImage = async (file, folder = 'question-bank-images') => {
  try {
    // Generate a unique filename
    const timestamp = new Date().getTime();
    const filename = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const storageRef = ref(storage, `${folder}/${filename}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image. Please try again.');
  }
};

/**
 * Upload multiple images to Firebase Storage
 * @param {File[]} files - Array of image files to upload
 * @param {string} folder - The folder path in Firebase Storage
 * @returns {Promise<string[]>} - Array of download URLs
 */
export const uploadMultipleImages = async (files, folder = 'question-bank-images') => {
  try {
    const uploadPromises = files.map(file => uploadImage(file, folder));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw new Error('Failed to upload images. Please try again.');
  }
};

/**
 * Callback function for react-draft-wysiwyg image upload
 * @param {File} file - The image file to upload
 * @returns {Promise<{data: {link: string}}>} - Object with image link
 */
export const uploadImageCallback = async (file) => {
  try {
    const specialty = store.getState()?.specialty?.activeSpecialty || 'pmr';
    const folder = getSpecialtyStorageFolder('question-bank-images', specialty);
    const optimizedFile = await optimizeImage(file, { maxW: 800, quality: 0.8 });
    const url = await uploadImage(optimizedFile, folder);
    return { data: { link: url } };
  } catch (error) {
    console.error('Error in uploadImageCallback:', error);
    throw error;
  }
};

/**
 * Delete an image from Firebase Storage by its download URL.
 * Fails silently if image doesn't exist or URL is not from our storage.
 * @param {string} downloadURL - The Firebase Storage download URL
 */
export const deleteImage = async (downloadURL) => {
  try {
    if (!downloadURL || !downloadURL.includes('firebasestorage.googleapis.com')) {
      return;
    }
    const pathMatch = downloadURL.match(/\/o\/(.+?)\?/);
    if (!pathMatch) return;

    const storagePath = decodeURIComponent(pathMatch[1]);
    const imageRef = ref(storage, storagePath);
    await deleteObject(imageRef);
  } catch (error) {
    console.warn('deleteImage: Failed to delete (may already be deleted):', error.message);
  }
};
